import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  InputAdornment,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import TableRowsIcon from "@mui/icons-material/TableRows";
import ViewAgendaOutlinedIcon from "@mui/icons-material/ViewAgendaOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import RouteIcon from "@mui/icons-material/Route";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import {
  useGetAllInvoiceQuery,
  useGetCompanyQuery,
  useGetDriverQuery,
  useGetWorkOrderQuery,
} from "../../../Api/attoDeskApi";
import { IInvoice } from "../../../Api/Interface/api.interface";
import { WorkOrderStatus } from "../../../Core/Enum/enum";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import moment from "moment";
import * as XLSX from "xlsx";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Pagination from "../../../Components/Pagination";
import VisibilitySharpIcon from "@mui/icons-material/VisibilitySharp";
import LocalPrintshopSharpIcon from "@mui/icons-material/LocalPrintshopSharp";
import DownloadIcon from "@mui/icons-material/Download";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import { CiSearch } from "react-icons/ci";

dayjs.extend(utc);
dayjs.extend(timezone);

interface Payment {
  id: number;
  work_order_id: number;
  amount: number;
  payment_date: string;
  payment_method: string;
  created_at: string;
  InvoiceNo: string;
  is_deleted: number;
  is_return?: number;
}

const STATUS_CFG: Record<number, { label: string; color: string; bg: string }> = {
  [WorkOrderStatus.Paid]:      { label: "Paid",      color: "#16a34a", bg: "#dcfce7" },
  [WorkOrderStatus.Cancel]:    { label: "Cancelled", color: "#dc2626", bg: "#fee2e2" },
  [WorkOrderStatus.Pending]:   { label: "Pending",   color: "#d97706", bg: "#fef3c7" },
  [WorkOrderStatus.Initiated]: { label: "Initiated", color: "#2563eb", bg: "#dbeafe" },
};
const getStatus = (code: number) => STATUS_CFG[code] ?? { label: "Unknown", color: "#64748b", bg: "#f1f5f9" };

const cap = (s?: string | null) => s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "";

const DATE_OPTIONS = [
  { label: "Today",      value: "today" },
  { label: "Yesterday",  value: "yesterday" },
  { label: "This Week",  value: "this_week" },
  { label: "Last Week",  value: "last_week" },
  { label: "This Month", value: "this_month" },
  { label: "Last Month", value: "last_month" },
  { label: "Custom",     value: "custom" },
];

const hdrSx = {
  fontWeight: 700, fontSize: 11, color: "#64748b",
  textTransform: "uppercase" as const, letterSpacing: "0.06em",
  bgcolor: "#f8fafc", py: 1.5, px: 2, borderBottom: "1px solid #e2e8f0",
  whiteSpace: "nowrap" as const,
};
const cellSx = { fontSize: 12, color: "#1e293b", py: 1.25, px: 2, borderBottom: "1px solid #f1f5f9" };

// ── Summary card ─────────────────────────────────────────────────────────────
const SummaryCard = ({ icon, title, value, color }: { icon: React.ReactNode; title: string; value: string | number; color: string }) => (
  <Card sx={{ borderRadius: 2.5, boxShadow: "0 1px 8px rgba(0,0,0,0.07)", border: "1px solid #f1f5f9", overflow: "hidden", height: "100%", display: "flex", flexDirection: "column" }}>
    <Box sx={{ height: 3, bgcolor: color, flexShrink: 0 }} />
    <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1.5, flex: 1, minHeight: 72 }}>
      <Box sx={{ width: 40, height: 40, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: `${color}18`, flexShrink: 0 }}>
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.6, whiteSpace: "nowrap" }}>{title}</Typography>
        <Typography sx={{ fontSize: 20, fontWeight: 800, color: "#0f172a", lineHeight: 1.2, mt: 0.25, whiteSpace: "nowrap" }}>{value}</Typography>
      </Box>
    </Box>
  </Card>
);

// ── Section card header ───────────────────────────────────────────────────────
const SectionHeader = ({ accent, title, actions }: { accent: string; title: string; actions?: React.ReactNode }) => (
  <Box sx={{ px: 2.5, py: 1.75, borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box sx={{ width: 3, height: 16, borderRadius: 2, bgcolor: accent }} />
      <Typography sx={{ fontWeight: 700, fontSize: 12, color: "#1e293b", textTransform: "uppercase", letterSpacing: 0.8 }}>{title}</Typography>
    </Box>
    {actions && <Box sx={{ display: "flex", gap: 1 }}>{actions}</Box>}
  </Box>
);

// ─── Component ────────────────────────────────────────────────────────────────
const Index = () => {
  const navigate = useNavigate();
  const componentRef = useRef<HTMLDivElement>(null);
  const paymentRef   = useRef<HTMLDivElement>(null);
  const hstRef       = useRef<HTMLDivElement>(null);
  const API_URL      = process.env.REACT_APP_API_URL;

  const [searchQuery,        setSearchQuery]        = useState("");
  const [selectedDriver,     setSelectedDriver]     = useState("");
  const [selectedCompany,    setSelectedCompany]    = useState("");
  const [dateFilter,         setDateFilter]         = useState("");
  const [startDate,          setStartDate]          = useState("");
  const [endDate,            setEndDate]            = useState("");
  const [payments,           setPayments]           = useState<Payment[]>([]);
  const [drivers,            setDrivers]            = useState<any[]>([]);
  const [page,               setPage]               = useState(0);
  const [rowsPerPage]                               = useState(15);
  const [viewMode,           setViewMode]           = useState<"flat" | "grouped">("flat");
  const [groupBy,            setGroupBy]            = useState<"client" | "company" | "driver" | "route">("client");
  const [expandedGroups,     setExpandedGroups]     = useState<Set<string>>(new Set());
  const [statusFilter,       setStatusFilter]       = useState<"" | "paid" | "pending" | "partial">("");
  const [insightsOpen,       setInsightsOpen]       = useState(true);
  const [pdfLoading,         setPdfLoading]         = useState(false);

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      const params: Record<string, string> = {};
      if (searchQuery.trim())   params.search      = searchQuery.trim();
      if (startDate)            params.start_date  = startDate;
      if (endDate)              params.end_date    = endDate;
      if (selectedCompany)      params.company_id  = selectedCompany;
      if (selectedDriver)       params.driver_id   = selectedDriver;
      if (statusFilter)         params.status      = statusFilter;
      const response = await axios.get(`${API_URL}/pdf/report-list`, {
        params, responseType: "blob",
        headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
      });
      const url  = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `trip-report-${new Date().toISOString().slice(0, 10)}.pdf`);
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch { /* silent */ } finally { setPdfLoading(false); }
  };

  const { data,        isLoading, isError, refetch } = useGetAllInvoiceQuery({ searchText: "", id: "", quoteWorkOrderCustomerId: "", quoteWorkOrderCustomerProjectId: "" });
  const { data: woData }                             = useGetWorkOrderQuery({ searchText: "", id: "" });
  const { data: companyData }                        = useGetCompanyQuery({ searchText: "", id: "" });
  const { data: driverData }                         = useGetDriverQuery({ searchText: "", id: "" });

  // Load drivers
  useEffect(() => {
    if (Array.isArray(driverData?.data))
      setDrivers((driverData!.data as any[]).filter((d) => d.driverUserRole === "driver"));
  }, [driverData]);

  // Fetch all payments
  useEffect(() => {
    axios.get(`${API_URL}/payments`)
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : [];
        setPayments(data.filter((p: Payment) => p.is_deleted === 0 && !p.is_return));
      })
      .catch(console.error);
  }, []);

  // Date range from preset filter
  useEffect(() => {
    if (dateFilter === "custom" || !dateFilter) return;
    const now = dayjs().tz("America/Toronto");
    let start = "", end = "";
    switch (dateFilter) {
      case "today":      start = end = now.format("YYYY-MM-DD"); break;
      case "yesterday":  start = end = now.subtract(1, "day").format("YYYY-MM-DD"); break;
      case "this_week":  start = now.startOf("week").format("YYYY-MM-DD"); end = now.endOf("week").format("YYYY-MM-DD"); break;
      case "last_week":  start = now.subtract(1,"week").startOf("week").format("YYYY-MM-DD"); end = now.subtract(1,"week").endOf("week").format("YYYY-MM-DD"); break;
      case "this_month": start = now.startOf("month").format("YYYY-MM-DD"); end = now.endOf("month").format("YYYY-MM-DD"); break;
      case "last_month": start = now.subtract(1,"month").startOf("month").format("YYYY-MM-DD"); end = now.subtract(1,"month").endOf("month").format("YYYY-MM-DD"); break;
    }
    setStartDate(start); setEndDate(end);
  }, [dateFilter]);

  // ── Merge direct invoices + converted work orders ──────────────────────────
  const coursings: IInvoice[] = (() => {
    const direct: IInvoice[] = Array.isArray(data?.data) ? (data!.data as IInvoice[]) : [];
    const directIds = new Set(direct.map((i) => String(i.work_order_id)));
    const fromWo: IInvoice[] = Array.isArray(woData?.data)
      ? (woData!.data as any[])
          .filter((wo) => (wo.isInvoice === true || wo.isInvoice == 1) && !directIds.has(String(wo.id)))
          .map((wo) => ({
            id: wo.invoiceId || wo.id,
            work_order_id: wo.id,
            InvoiceNo: String(wo.InvoiceNo || ""),
            quoteWorkOrderSaleGrandTotal: wo.quoteWorkOrderSaleGrandTotal,
            quoteWorkOrderStatus: wo.quoteWorkOrderStatus,
            quoteWorkOrderCustomerId: wo.quoteWorkOrderCustomerId,
            amount_paid: wo.amount_paid,
            customer: wo.customer,
            company: wo.company,
            project: wo.project,
            drivers: wo.drivers,
            assign_driver: wo.assign_driver,
            quoteWorkOrderSale: wo.quoteWorkOrderSale,
            created_at: wo.created_at,
            quoteWorkOrderNo: wo.quoteWorkOrderNo,
          } as IInvoice))
      : [];
    return [...direct, ...fromWo];
  })();

  const inRange = (dateStr?: string) => {
    if (!dateFilter || !dateStr) return true;
    const d = new Date(dateStr).toLocaleDateString("en-CA");
    const s = new Date(startDate).toLocaleDateString("en-CA");
    const e = endDate ? new Date(endDate).toLocaleDateString("en-CA") : s;
    return d >= s && d <= e;
  };

  const q = searchQuery.trim().toLowerCase();

  const filteredCoursings = coursings.filter((row) => {
    if (selectedDriver && !(Array.isArray(row?.drivers) && row.drivers.some((d: any) => d.id === selectedDriver))) return false;
    if (selectedCompany) {
      const match = Array.isArray(row.company)
        ? (row.company as any[]).some((c) => String(c.id) === String(selectedCompany))
        : String((row.company as any)?.id) === String(selectedCompany);
      if (!match) return false;
    }
    if (!inRange(row.created_at)) return false;
    if (q) {
      const inv  = (row.InvoiceNo || "").toLowerCase();
      const name = `${cap(row.customer?.customerFirstName)} ${cap(row.customer?.customerLastName)}`.toLowerCase();
      const comp = (row.customer?.customerCompanyName || "").toLowerCase();
      const proj = (row.project?.projectName || "").toLowerCase();
      if (!inv.includes(q) && !name.includes(q) && !comp.includes(q) && !proj.includes(q)) return false;
    }
    if (statusFilter) {
      const total  = parseFloat(row.quoteWorkOrderSaleGrandTotal || "0");
      const paid   = parseFloat(String(row.amount_paid || "0"));
      const isPaid = total > 0 && paid >= total;
      const isPartial = paid > 0 && !isPaid;
      if (statusFilter === "paid"    && !isPaid)                   return false;
      if (statusFilter === "pending" && (isPaid || isPartial))     return false;
      if (statusFilter === "partial" && !isPartial)                return false;
    }
    return true;
  });

  // ── Grouped view data ──────────────────────────────────────────────────────
  const toggleGroup = (key: string) =>
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const groupedData = useMemo(() => {
    if (viewMode !== "grouped") return [];
    type Group = { key: string; invoices: IInvoice[]; totalAmount: number; totalPaid: number; totalHST: number };
    const groups: Record<string, Group> = {};
    filteredCoursings.forEach((inv) => {
      let key = "";
      switch (groupBy) {
        case "client":
          key = inv.project?.projectName || inv.customer?.customerCompanyName
            || `${cap(inv.customer?.customerFirstName)} ${cap(inv.customer?.customerLastName)}`.trim() || "Unknown";
          break;
        case "company":
          key = (inv.company as any)?.companyName || "Unknown Company";
          break;
        case "driver":
          key = Array.isArray(inv.drivers) && inv.drivers.length
            ? inv.drivers.map((d: any) => `${cap(d.driverFirstName)} ${cap(d.driverLastName)}`).join(", ")
            : "Unassigned";
          break;
        case "route": {
          const s0 = Array.isArray(inv.quoteWorkOrderSale) && inv.quoteWorkOrderSale.length
            ? (inv.quoteWorkOrderSale[0] as any) : null;
          key = s0?.transferTypeFrom && s0?.transferTypeTo
            ? `${s0.transferTypeFrom} → ${s0.transferTypeTo}`
            : "Unknown Route";
          break;
        }
      }
      if (!groups[key]) groups[key] = { key, invoices: [], totalAmount: 0, totalPaid: 0, totalHST: 0 };
      groups[key].invoices.push(inv);
      groups[key].totalAmount += parseFloat(inv.quoteWorkOrderSaleGrandTotal || "0");
      groups[key].totalPaid   += parseFloat(String(inv.amount_paid || "0"));
      groups[key].totalHST    += Array.isArray(inv.quoteWorkOrderSale)
        ? inv.quoteWorkOrderSale.reduce((ts: number, p: any) => ts + Number(p.taxAmount || 0), 0) : 0;
    });
    return Object.values(groups).sort((a, b) => b.totalAmount - a.totalAmount);
  }, [filteredCoursings, viewMode, groupBy]);

  // ── Export grouped Excel ────────────────────────────────────────────────────
  const exportGroupedExcel = () => {
    if (!groupedData.length) return;
    const rows: any[] = [];
    groupedData.forEach((g) => {
      rows.push({ Group: g.key, "Invoice No": "", "Date": "", "Amount": "", "Paid": "", "Balance": "", "HST": "" });
      g.invoices.forEach((inv) => {
        const total   = parseFloat(inv.quoteWorkOrderSaleGrandTotal || "0");
        const paid    = parseFloat(String(inv.amount_paid || "0"));
        const rowHST  = Array.isArray(inv.quoteWorkOrderSale)
          ? inv.quoteWorkOrderSale.reduce((ts: number, p: any) => ts + Number(p.taxAmount || 0), 0) : 0;
        rows.push({
          Group: "",
          "Invoice No": inv.InvoiceNo || "—",
          "Date": moment(inv.created_at).format("DD-MMM-YY"),
          "Amount": `$${total.toFixed(2)}`,
          "Paid": `$${paid.toFixed(2)}`,
          "Balance": `$${Math.max(0, total - paid).toFixed(2)}`,
          "HST": `$${rowHST.toFixed(2)}`,
        });
      });
      rows.push({ Group: "SUBTOTAL", "Invoice No": `${g.invoices.length} invoices`, "Date": "", "Amount": `$${g.totalAmount.toFixed(2)}`, "Paid": `$${g.totalPaid.toFixed(2)}`, "Balance": `$${Math.max(0, g.totalAmount - g.totalPaid).toFixed(2)}`, "HST": `$${g.totalHST.toFixed(2)}` });
      rows.push({ Group: "", "Invoice No": "", "Date": "", "Amount": "", "Paid": "", "Balance": "", "HST": "" });
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `By ${groupBy}`);
    XLSX.writeFile(wb, `tjbus_report_by_${groupBy}.xlsx`);
  };

  const filteredPayments = payments.filter((p) => {
    // Only join against coursings when a driver or company filter is active;
    // without those filters every non-deleted payment should be counted.
    if (selectedDriver || selectedCompany) {
      const inv = coursings.find((i) => String(i.work_order_id) === String(p.work_order_id));
      if (!inv) return false;
      if (selectedDriver && !(Array.isArray(inv?.drivers) && inv.drivers.some((d: any) => d.id === selectedDriver))) return false;
      if (selectedCompany) {
        const match = Array.isArray(inv.company)
          ? (inv.company as any[]).some((c) => String(c.id) === String(selectedCompany))
          : String((inv.company as any)?.id) === String(selectedCompany);
        if (!match) return false;
      }
    }
    return inRange(p.payment_date);
  });

  // ── Summary numbers ────────────────────────────────────────────────────────
  const totalPaymentsSum = filteredPayments.reduce((s, p) => s + Number(p.amount || 0), 0);
  const totalCash   = filteredPayments.filter((p) => /^cash$/i.test(p.payment_method || "")).reduce((s, p) => s + Number(p.amount || 0), 0);
  const totalCreditCard = filteredPayments.filter((p) => /^(cc|credit|credit\s*card|debit|debit\s*card|bambora)/i.test(p.payment_method || "")).reduce((s, p) => s + Number(p.amount || 0), 0);
  const totalPO     = filteredPayments.filter((p) => /po|purchase/i.test(p.payment_method || "")).reduce((s, p) => s + Number(p.amount || 0), 0);
  const totalClients = new Set(filteredCoursings.map((i) => i.quoteWorkOrderCustomerId)).size;
  const totalHST = filteredCoursings.reduce((s, row) =>
    s + (Array.isArray(row.quoteWorkOrderSale)
      ? row.quoteWorkOrderSale.reduce((ts: number, p: any) => ts + Number(p.taxAmount || 0), 0)
      : 0), 0);

  // ── Analytics (all derived from filteredCoursings/filteredPayments) ─────────

  const clientStats = useMemo(() => {
    const map: Record<string, { name: string; trips: number; revenue: number; paid: number; lastDate: string }> = {};
    filteredCoursings.forEach((inv) => {
      const name = inv.project?.projectName || inv.customer?.customerCompanyName
        || `${cap(inv.customer?.customerFirstName)} ${cap(inv.customer?.customerLastName)}`.trim() || "Unknown";
      const id   = String(inv.quoteWorkOrderCustomerId || name);
      if (!map[id]) map[id] = { name, trips: 0, revenue: 0, paid: 0, lastDate: "" };
      map[id].trips++;
      map[id].revenue += parseFloat(inv.quoteWorkOrderSaleGrandTotal || "0");
      map[id].paid    += parseFloat(String(inv.amount_paid || "0"));
      if (!map[id].lastDate || inv.created_at > map[id].lastDate) map[id].lastDate = inv.created_at || "";
    });
    return Object.values(map).sort((a, b) => b.revenue - a.revenue);
  }, [filteredCoursings]);

  const driverStats = useMemo(() => {
    const map: Record<string, { name: string; trips: number; revenue: number }> = {};
    filteredCoursings.forEach((inv) => {
      if (!Array.isArray(inv.drivers) || !inv.drivers.length) {
        const key = "Unassigned";
        if (!map[key]) map[key] = { name: key, trips: 0, revenue: 0 };
        map[key].trips++;
        return;
      }
      inv.drivers.forEach((d: any) => {
        const name = `${cap(d.driverFirstName)} ${cap(d.driverLastName)}`.trim() || "Unknown";
        if (!map[name]) map[name] = { name, trips: 0, revenue: 0 };
        map[name].trips++;
        map[name].revenue += parseFloat(inv.quoteWorkOrderSaleGrandTotal || "0") / (inv.drivers as unknown as any[]).length;
      });
    });
    return Object.values(map).filter(d => d.name !== "Unassigned").sort((a, b) => b.trips - a.trips);
  }, [filteredCoursings]);

  const routeStats = useMemo(() => {
    const map: Record<string, { route: string; trips: number; revenue: number }> = {};
    filteredCoursings.forEach((inv) => {
      if (!Array.isArray(inv.quoteWorkOrderSale)) return;
      (inv.quoteWorkOrderSale as any[]).forEach((s: any) => {
        if (!s?.transferTypeFrom || !s?.transferTypeTo) return;
        const route = `${s.transferTypeFrom} → ${s.transferTypeTo}`;
        if (!map[route]) map[route] = { route, trips: 0, revenue: 0 };
        map[route].trips++;
        map[route].revenue += parseFloat(inv.quoteWorkOrderSaleGrandTotal || "0");
      });
    });
    return Object.values(map).sort((a, b) => b.trips - a.trips).slice(0, 6);
  }, [filteredCoursings]);

  const revenueByDay = useMemo(() => {
    const map: Record<string, number> = {};
    filteredCoursings.forEach((inv) => {
      const day = moment(inv.created_at).format("YYYY-MM-DD");
      map[day] = (map[day] || 0) + parseFloat(inv.quoteWorkOrderSaleGrandTotal || "0");
    });
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  }, [filteredCoursings]);

  const forecast30 = useMemo(() => {
    if (revenueByDay.length < 3) return null;
    const recent = revenueByDay.slice(-30);
    const avg = recent.reduce((s, [, v]) => s + v, 0) / recent.length;
    return avg * 30;
  }, [revenueByDay]);

  const alerts = useMemo(() => {
    const list: { type: "red" | "yellow" | "green"; msg: string }[] = [];
    const outstanding = clientStats.filter(c => c.revenue - c.paid > 500);
    if (outstanding.length) list.push({ type: "red", msg: `${outstanding.length} client${outstanding.length > 1 ? "s" : ""} have outstanding balances over $500 (${outstanding.slice(0, 2).map(c => c.name).join(", ")}${outstanding.length > 2 ? "…" : ""})` });
    const totalBalance = filteredCoursings.reduce((s, i) => s + Math.max(0, parseFloat(i.quoteWorkOrderSaleGrandTotal || "0") - parseFloat(String(i.amount_paid || "0"))), 0);
    if (totalBalance > 1000) list.push({ type: "red", msg: `Total outstanding balance is $${totalBalance.toFixed(2)} — follow up with clients` });
    const thirtyDaysAgo = moment().subtract(30, "days").format("YYYY-MM-DD");
    const inactive = clientStats.filter(c => c.lastDate < thirtyDaysAgo);
    if (inactive.length) list.push({ type: "yellow", msg: `${inactive.length} client${inactive.length > 1 ? "s" : ""} haven't booked in 30+ days` });
    if (clientStats.length > 0) list.push({ type: "green", msg: `Top client: ${clientStats[0].name} with $${clientStats[0].revenue.toFixed(2)} revenue` });
    if (driverStats.length > 0) list.push({ type: "green", msg: `Most active driver: ${driverStats[0].name} with ${driverStats[0].trips} trip${driverStats[0].trips !== 1 ? "s" : ""}` });
    return list;
  }, [clientStats, driverStats, filteredCoursings]);

  const aiInsights = useMemo(() => {
    const msgs: string[] = [];
    const total = filteredCoursings.reduce((s, i) => s + parseFloat(i.quoteWorkOrderSaleGrandTotal || "0"), 0);
    if (clientStats.length > 0 && total > 0) {
      const topPct = ((clientStats[0].revenue / total) * 100).toFixed(0);
      msgs.push(`${clientStats[0].name} generated ${topPct}% of total revenue in the selected period`);
    }
    if (routeStats.length > 0) msgs.push(`Most active route: ${routeStats[0].route} (${routeStats[0].trips} trip${routeStats[0].trips !== 1 ? "s" : ""})`);
    if (driverStats.length > 0) msgs.push(`${driverStats[0].name} handled the most trips (${driverStats[0].trips})`);
    const outstanding = clientStats.filter(c => c.revenue - c.paid > 500).length;
    if (outstanding > 0) msgs.push(`${outstanding} client${outstanding > 1 ? "s have" : " has"} outstanding balance${outstanding > 1 ? "s" : ""} over $500`);
    if (forecast30 !== null) msgs.push(`Estimated revenue next 30 days: $${forecast30.toFixed(2)} (based on recent trend)`);
    if (totalPaymentsSum > 0) {
      const topMethod = totalCash >= totalCreditCard && totalCash >= totalPO ? "Cash"
        : totalCreditCard >= totalPO ? "Credit/Card" : "Purchase Order";
      msgs.push(`Most used payment method: ${topMethod}`);
    }
    return msgs;
  }, [clientStats, routeStats, driverStats, forecast30, filteredCoursings, totalPaymentsSum, totalCash, totalCreditCard, totalPO]);

  // ── Highcharts options ───────────────────────────────────────────────────────

  const revenueChartOpts: Highcharts.Options = useMemo(() => ({
    chart: { type: "area", height: 200, backgroundColor: "transparent", margin: [10, 10, 40, 50] },
    title: { text: undefined },
    credits: { enabled: false },
    legend: { enabled: false },
    xAxis: { categories: revenueByDay.map(([d]) => moment(d).format("DD MMM")), labels: { style: { fontSize: "10px", color: "#94a3b8" } }, tickLength: 0, lineColor: "#e2e8f0" },
    yAxis: { title: { text: undefined }, gridLineColor: "#f1f5f9", labels: { style: { fontSize: "10px", color: "#94a3b8" }, formatter() { return `$${(this.value as number / 1000).toFixed(0)}k`; } } },
    tooltip: { valuePrefix: "$", valueDecimals: 2, backgroundColor: "#1e293b", style: { color: "#fff" }, borderWidth: 0 },
    series: [{ type: "area", name: "Revenue", data: revenueByDay.map(([, v]) => v), color: "#2f3891", fillColor: { linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 }, stops: [[0, "rgba(124,58,237,0.3)"], [1, "rgba(124,58,237,0)"]] } }],
  }), [revenueByDay]);

  const paymentPieOpts: Highcharts.Options = useMemo(() => ({
    chart: { type: "pie", height: 200, backgroundColor: "transparent", margin: [0, 0, 0, 0] },
    title: { text: undefined },
    credits: { enabled: false },
    tooltip: { valuePrefix: "$", valueDecimals: 2, backgroundColor: "#1e293b", style: { color: "#fff" }, borderWidth: 0 },
    plotOptions: { pie: { dataLabels: { enabled: true, style: { fontSize: "10px", color: "#475569", fontWeight: "600" }, distance: 10 }, innerSize: "55%" } },
    series: [{ type: "pie", name: "Payments", data: [
      { name: "Cash", y: totalCash, color: "#16a34a" },
      { name: "Credit/Card", y: totalCreditCard, color: "#2563eb" },
      { name: "Purchase Order", y: totalPO, color: "#d97706" },
    ].filter(d => d.y > 0) }],
  }), [totalCash, totalCreditCard, totalPO]);

  const topClientsChartOpts: Highcharts.Options = useMemo(() => ({
    chart: { type: "bar", height: 200, backgroundColor: "transparent", margin: [10, 20, 10, 120] },
    title: { text: undefined },
    credits: { enabled: false },
    legend: { enabled: false },
    xAxis: { categories: clientStats.slice(0, 5).map(c => c.name.length > 16 ? c.name.slice(0, 14) + "…" : c.name), labels: { style: { fontSize: "10px", color: "#475569" } } },
    yAxis: { title: { text: undefined }, gridLineColor: "#f1f5f9", labels: { style: { fontSize: "10px", color: "#94a3b8" }, formatter() { return `$${(this.value as number / 1000).toFixed(0)}k`; } } },
    tooltip: { valuePrefix: "$", valueDecimals: 2, backgroundColor: "#1e293b", style: { color: "#fff" }, borderWidth: 0 },
    series: [{ type: "bar", name: "Revenue", data: clientStats.slice(0, 5).map(c => c.revenue), color: "#2f3891", borderRadius: 3 }],
  }), [clientStats]);

  // ── Excel exports ──────────────────────────────────────────────────────────
  const exportInvoicesExcel = () => {
    if (!filteredCoursings.length) return;
    const rows = filteredCoursings.map((row, i) => {
      const rowHST = Array.isArray(row.quoteWorkOrderSale)
        ? row.quoteWorkOrderSale.reduce((ts: number, p: any) => ts + Number(p.taxAmount || 0), 0)
        : 0;
      return {
        "#": i + 1,
        "Invoice No": row.InvoiceNo,
        "Status": getStatus(row.quoteWorkOrderStatus).label,
        "Amount": `$${parseFloat(row.quoteWorkOrderSaleGrandTotal || "0").toFixed(2)}`,
        "HST Tax": `$${rowHST.toFixed(2)}`,
        "Paid": `$${parseFloat(String(row.amount_paid || "0")).toFixed(2)}`,
        "Balance": `$${Math.max(0, parseFloat(row.quoteWorkOrderSaleGrandTotal || "0") - parseFloat(String(row.amount_paid || "0"))).toFixed(2)}`,
        "Client": `${cap(row.customer?.customerFirstName)} ${cap(row.customer?.customerLastName)}`.trim() || row.project?.projectName || "",
        "Company": (row.company as any)?.companyName || "",
      };
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Invoices");
    XLSX.writeFile(wb, "tjbus_invoices.xlsx");
  };

  const exportPaymentsExcel = () => {
    if (!filteredPayments.length) return;
    const rows = filteredPayments.map((p) => ({
      Date: moment(p.payment_date).format("DD-MMM-YY"),
      "Invoice No": p.InvoiceNo,
      Amount: `$${parseFloat(String(p.amount || "0")).toFixed(2)}`,
      Method: p.payment_method,
    }));
    rows.push({ Date: "", "Invoice No": "TOTAL", Amount: `$${totalPaymentsSum.toFixed(2)}`, Method: "" });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Payments");
    XLSX.writeFile(wb, "tjbus_payments.xlsx");
  };

  if (isLoading) return (
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "40vh" }}>
      <CircularProgress sx={{ color: "#2f3891" }} />
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <Card sx={{ borderRadius: 3, overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,0.07)", border: "1px solid #e2e8f0", mb: 3 }}>
        <Box sx={{ height: 4, background: "linear-gradient(90deg,#2f3891,#2563eb,#0ea5e9)" }} />
        <Box sx={{ px: 3, py: 2, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 18, color: "#0f172a" }}>Reports</Typography>
            <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 0.25 }}>Invoice &amp; payment summary with filters</Typography>
          </Box>
        </Box>
      </Card>

      {/* ── Business Intelligence Dashboard ───────────────────────────────── */}
      <Card sx={{ borderRadius: 2.5, boxShadow: "0 1px 8px rgba(0,0,0,0.07)", border: "1px solid #e2e8f0", mb: 3, overflow: "hidden" }}>
        {/* Toggle header */}
        <Box onClick={() => setInsightsOpen(o => !o)} sx={{ px: 2.5, py: 1.75, display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", bgcolor: insightsOpen ? "#faf5ff" : "#fff", borderBottom: insightsOpen ? "1px solid #e2e8f0" : "none", "&:hover": { bgcolor: "#faf5ff" } }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ width: 32, height: 32, borderRadius: 1.5, background: "linear-gradient(135deg,#2f3891,#2563eb)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <LightbulbOutlinedIcon sx={{ color: "#fff", fontSize: 16 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 13, color: "#1e293b", lineHeight: 1.2 }}>Business Intelligence</Typography>
              <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.2 }}>AI-powered insights, charts &amp; forecasting</Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {forecast30 !== null && (
              <Chip label={`Forecast: $${forecast30.toFixed(0)}/30d`} size="small" icon={<TrendingUpIcon sx={{ fontSize: 12, color: "#16a34a !important" }} />}
                sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: "#dcfce7", color: "#15803d", border: "1px solid #bbf7d0" }} />
            )}
            <Chip label={`${aiInsights.length} insights`} size="small" sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: "#ede9fe", color: "#252e7a" }} />
            {insightsOpen ? <ExpandLessIcon sx={{ fontSize: 18, color: "#94a3b8" }} /> : <ExpandMoreIcon sx={{ fontSize: 18, color: "#94a3b8" }} />}
          </Box>
        </Box>

        <Collapse in={insightsOpen}>
          <Box sx={{ p: 2.5 }}>

            {/* ── Alerts row ─────────────────────────────────────────── */}
            {alerts.length > 0 && (
              <Box sx={{ mb: 2.5, display: "flex", flexDirection: "column", gap: 1 }}>
                {alerts.map((a, i) => (
                  <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.25, borderRadius: 2,
                    bgcolor: a.type === "red" ? "#fef2f2" : a.type === "yellow" ? "#fffbeb" : "#f0fdf4",
                    border: `1px solid ${a.type === "red" ? "#fecaca" : a.type === "yellow" ? "#fde68a" : "#bbf7d0"}` }}>
                    {a.type === "red"    && <WarningAmberIcon sx={{ fontSize: 16, color: "#dc2626", flexShrink: 0 }} />}
                    {a.type === "yellow" && <WarningAmberIcon sx={{ fontSize: 16, color: "#d97706", flexShrink: 0 }} />}
                    {a.type === "green"  && <CheckCircleOutlineIcon sx={{ fontSize: 16, color: "#16a34a", flexShrink: 0 }} />}
                    <Typography sx={{ fontSize: 12.5, color: a.type === "red" ? "#991b1b" : a.type === "yellow" ? "#92400e" : "#14532d", fontWeight: 500 }}>
                      {a.msg}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {/* ── AI Insight chips ───────────────────────────────────── */}
            {aiInsights.length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2.5 }}>
                {aiInsights.map((msg, i) => (
                  <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 0.75, px: 1.5, py: 0.75, borderRadius: 20, bgcolor: "#f8f4ff", border: "1px solid #ddd6fe" }}>
                    <LightbulbOutlinedIcon sx={{ fontSize: 13, color: "#2f3891" }} />
                    <Typography sx={{ fontSize: 12, color: "#4c1d95", fontWeight: 500 }}>{msg}</Typography>
                  </Box>
                ))}
              </Box>
            )}

            {/* ── Charts row ─────────────────────────────────────────── */}
            <Grid container spacing={2} sx={{ mb: 2.5 }}>
              {/* Revenue trend */}
              <Grid item xs={12} md={5}>
                <Box sx={{ bgcolor: "#fff", borderRadius: 2, border: "1px solid #f1f5f9", p: 1.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
                    <TrendingUpIcon sx={{ fontSize: 14, color: "#2f3891" }} />
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#1e293b" }}>Revenue Trend</Typography>
                  </Box>
                  {revenueByDay.length > 1
                    ? <HighchartsReact highcharts={Highcharts} options={revenueChartOpts} />
                    : <Box sx={{ height: 160, display: "flex", alignItems: "center", justifyContent: "center" }}><Typography sx={{ fontSize: 12, color: "#94a3b8" }}>Not enough data</Typography></Box>
                  }
                </Box>
              </Grid>

              {/* Payment breakdown */}
              <Grid item xs={12} md={3}>
                <Box sx={{ bgcolor: "#fff", borderRadius: 2, border: "1px solid #f1f5f9", p: 1.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
                    <PaymentsOutlinedIcon sx={{ fontSize: 14, color: "#2563eb" }} />
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#1e293b" }}>Payment Methods</Typography>
                  </Box>
                  {(totalCash + totalCreditCard + totalPO) > 0
                    ? <HighchartsReact highcharts={Highcharts} options={paymentPieOpts} />
                    : <Box sx={{ height: 160, display: "flex", alignItems: "center", justifyContent: "center" }}><Typography sx={{ fontSize: 12, color: "#94a3b8" }}>No payments yet</Typography></Box>
                  }
                </Box>
              </Grid>

              {/* Top clients */}
              <Grid item xs={12} md={4}>
                <Box sx={{ bgcolor: "#fff", borderRadius: 2, border: "1px solid #f1f5f9", p: 1.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
                    <EmojiEventsOutlinedIcon sx={{ fontSize: 14, color: "#d97706" }} />
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#1e293b" }}>Top Clients by Revenue</Typography>
                  </Box>
                  {clientStats.length > 0
                    ? <HighchartsReact highcharts={Highcharts} options={topClientsChartOpts} />
                    : <Box sx={{ height: 160, display: "flex", alignItems: "center", justifyContent: "center" }}><Typography sx={{ fontSize: 12, color: "#94a3b8" }}>No data</Typography></Box>
                  }
                </Box>
              </Grid>
            </Grid>

            {/* ── Driver & Route analytics row ───────────────────────── */}
            <Grid container spacing={2}>
              {/* Driver performance */}
              <Grid item xs={12} md={6}>
                <Box sx={{ bgcolor: "#fff", borderRadius: 2, border: "1px solid #f1f5f9", overflow: "hidden" }}>
                  <Box sx={{ px: 2, py: 1.25, borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 0.75 }}>
                    <DirectionsBusIcon sx={{ fontSize: 14, color: "#2f3891" }} />
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#1e293b" }}>Driver Performance</Typography>
                  </Box>
                  {driverStats.length === 0
                    ? <Box sx={{ p: 3, textAlign: "center" }}><Typography sx={{ fontSize: 12, color: "#94a3b8" }}>No driver data</Typography></Box>
                    : <Table size="small">
                        <TableHead>
                          <TableRow>
                            {["Driver", "Trips", "Revenue", "Share"].map(h => <TableCell key={h} sx={{ ...hdrSx, py: 1 }}>{h}</TableCell>)}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {driverStats.slice(0, 5).map((d, i) => {
                            const totalTrips = driverStats.reduce((s, x) => s + x.trips, 0);
                            const pct = totalTrips > 0 ? ((d.trips / totalTrips) * 100).toFixed(0) : "0";
                            return (
                              <TableRow key={d.name} sx={{ "&:hover": { bgcolor: "#f8faff" } }}>
                                <TableCell sx={cellSx}>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                    <Box sx={{ width: 20, height: 20, borderRadius: "50%", bgcolor: i === 0 ? "#fef3c7" : "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                      <Typography sx={{ fontSize: 9, fontWeight: 800, color: i === 0 ? "#d97706" : "#64748b" }}>{i + 1}</Typography>
                                    </Box>
                                    {d.name}
                                  </Box>
                                </TableCell>
                                <TableCell sx={cellSx}>{d.trips}</TableCell>
                                <TableCell sx={{ ...cellSx, color: "#16a34a", fontWeight: 600 }}>${d.revenue.toFixed(0)}</TableCell>
                                <TableCell sx={cellSx}>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                    <Box sx={{ flex: 1, height: 4, borderRadius: 2, bgcolor: "#f1f5f9" }}>
                                      <Box sx={{ height: 4, borderRadius: 2, bgcolor: "#2f3891", width: `${pct}%` }} />
                                    </Box>
                                    <Typography sx={{ fontSize: 10, color: "#94a3b8", minWidth: 28 }}>{pct}%</Typography>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                  }
                </Box>
              </Grid>

              {/* Route analysis */}
              <Grid item xs={12} md={6}>
                <Box sx={{ bgcolor: "#fff", borderRadius: 2, border: "1px solid #f1f5f9", overflow: "hidden" }}>
                  <Box sx={{ px: 2, py: 1.25, borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 0.75 }}>
                    <RouteIcon sx={{ fontSize: 14, color: "#ea580c" }} />
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#1e293b" }}>Route Analysis</Typography>
                  </Box>
                  {routeStats.length === 0
                    ? <Box sx={{ p: 3, textAlign: "center" }}><Typography sx={{ fontSize: 12, color: "#94a3b8" }}>No route data</Typography></Box>
                    : <Table size="small">
                        <TableHead>
                          <TableRow>
                            {["Route", "Trips", "Revenue"].map(h => <TableCell key={h} sx={{ ...hdrSx, py: 1 }}>{h}</TableCell>)}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {routeStats.map((r, i) => (
                            <TableRow key={r.route} sx={{ "&:hover": { bgcolor: "#fff7ed" } }}>
                              <TableCell sx={{ ...cellSx, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                  {i === 0 && <TrendingUpIcon sx={{ fontSize: 12, color: "#16a34a" }} />}
                                  {r.route}
                                </Box>
                              </TableCell>
                              <TableCell sx={cellSx}>{r.trips}</TableCell>
                              <TableCell sx={{ ...cellSx, color: "#ea580c", fontWeight: 600 }}>${r.revenue.toFixed(0)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                  }
                </Box>
              </Grid>
            </Grid>

          </Box>
        </Collapse>
      </Card>

      {/* ── Filters ───────────────────────────────────────────────────────── */}
      <Card sx={{ borderRadius: 2.5, boxShadow: "0 1px 8px rgba(0,0,0,0.06)", border: "1px solid #f1f5f9", mb: 3 }}>
        <Box sx={{ p: 2 }}>
          {/* Date preset buttons */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 2 }}>
            {DATE_OPTIONS.map((opt) => (
              <Button key={opt.value} size="small" variant={dateFilter === opt.value ? "contained" : "outlined"}
                onClick={() => setDateFilter(opt.value)}
                sx={{ textTransform: "none", fontSize: 12, borderRadius: 1.5, fontWeight: 600,
                  ...(dateFilter === opt.value
                    ? { bgcolor: "#2f3891", "&:hover": { bgcolor: "#252e7a" }, boxShadow: "none" }
                    : { borderColor: "#e2e8f0", color: "#475569", "&:hover": { borderColor: "#c4b5fd", bgcolor: "#faf5ff" } }) }}>
                {opt.label}
              </Button>
            ))}
            <Button size="small" variant="outlined" startIcon={<RestartAltIcon sx={{ fontSize: 13 }} />}
              onClick={() => { setDateFilter(""); setStartDate(""); setEndDate(""); setSelectedDriver(""); setSelectedCompany(""); setSearchQuery(""); }}
              sx={{ textTransform: "none", fontSize: 12, borderRadius: 1.5, borderColor: "#e2e8f0", color: "#64748b", ml: "auto" }}>
              Reset
            </Button>
          </Box>

          {/* Custom date + dropdown filters */}
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-end" }}>
            {dateFilter === "custom" && (
              <>
                <Box>
                  <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#475569", mb: 0.5 }}>Start Date</Typography>
                  <TextField type="date" size="small" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: 13 }, width: 160 }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#475569", mb: 0.5 }}>End Date</Typography>
                  <TextField type="date" size="small" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: 13 }, width: 160 }} />
                </Box>
              </>
            )}

            <Box>
              <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#475569", mb: 0.5 }}>Driver</Typography>
              <Select size="small" displayEmpty value={selectedDriver} onChange={(e) => setSelectedDriver(e.target.value)}
                sx={{ width: 180, borderRadius: 1.5, fontSize: 13 }}>
                <MenuItem value=""><em style={{ fontStyle: "normal", color: "#94a3b8" }}>All Drivers</em></MenuItem>
                {drivers.map((d) => (
                  <MenuItem key={d.id} value={d.id} sx={{ fontSize: 13 }}>{cap(d.driverFirstName)} {cap(d.driverLastName)}</MenuItem>
                ))}
              </Select>
            </Box>

            <Box>
              <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#475569", mb: 0.5 }}>Company</Typography>
              <Select size="small" displayEmpty value={selectedCompany} onChange={(e) => setSelectedCompany(e.target.value)}
                sx={{ width: 200, borderRadius: 1.5, fontSize: 13 }}>
                <MenuItem value=""><em style={{ fontStyle: "normal", color: "#94a3b8" }}>All Companies</em></MenuItem>
                {Array.isArray(companyData?.data) && (companyData!.data as any[]).map((c) => (
                  <MenuItem key={c.id} value={c.id} sx={{ fontSize: 13 }}>{c.companyName}</MenuItem>
                ))}
              </Select>
            </Box>

            <Box>
              <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#475569", mb: 0.5 }}>Status</Typography>
              <Select size="small" displayEmpty value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as any)}
                sx={{ width: 150, borderRadius: 1.5, fontSize: 13 }}>
                <MenuItem value=""><em style={{ fontStyle: "normal", color: "#94a3b8" }}>All Statuses</em></MenuItem>
                <MenuItem value="paid"    sx={{ fontSize: 13 }}>Paid</MenuItem>
                <MenuItem value="partial" sx={{ fontSize: 13 }}>Partial</MenuItem>
                <MenuItem value="pending" sx={{ fontSize: 13 }}>Unpaid</MenuItem>
              </Select>
            </Box>

            <Box sx={{ ml: { md: "auto" } }}>
              <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#475569", mb: 0.5 }}>Search</Typography>
              <TextField size="small" placeholder="Invoice no, client…" value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><CiSearch style={{ fontSize: 16, color: "#94a3b8" }} /></InputAdornment> }}
                sx={{ width: 220, "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: 13 } }} />
            </Box>
          </Box>
        </Box>
      </Card>

      {/* ── Summary cards ─────────────────────────────────────────────────── */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(4, 1fr)", lg: "repeat(7, 1fr)" }, gap: 2, mb: 3 }}>
        {[
          { icon: <ReceiptLongOutlinedIcon sx={{ fontSize: 18, color: "#2f3891" }} />, title: "Total Invoices",  value: filteredCoursings.length, color: "#2f3891" },
          { icon: <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 18, color: "#2563eb" }} />, title: "Total Payments", value: `$${totalPaymentsSum.toFixed(2)}`, color: "#2563eb" },
          { icon: <PaymentsOutlinedIcon sx={{ fontSize: 18, color: "#16a34a" }} />, title: "Cash",           value: `$${totalCash.toFixed(2)}`, color: "#16a34a" },
          { icon: <PaymentsOutlinedIcon sx={{ fontSize: 18, color: "#0ea5e9" }} />, title: "Credit / Card",  value: `$${totalCreditCard.toFixed(2)}`, color: "#0ea5e9" },
          { icon: <PaymentsOutlinedIcon sx={{ fontSize: 18, color: "#d97706" }} />, title: "Purchase Order", value: `$${totalPO.toFixed(2)}`, color: "#d97706" },
          { icon: <PaymentsOutlinedIcon sx={{ fontSize: 18, color: "#ea580c" }} />, title: "HST Tax",        value: `$${totalHST.toFixed(2)}`, color: "#ea580c" },
          { icon: <PeopleAltOutlinedIcon sx={{ fontSize: 18, color: "#db2777" }} />, title: "Unique Clients", value: totalClients, color: "#db2777" },
        ].map((card, i) => (
          <SummaryCard key={i} {...card} />
        ))}
      </Box>

      {/* ── View toggle ───────────────────────────────────────────────────── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 1.5 }}>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            size="small"
            variant={viewMode === "flat" ? "contained" : "outlined"}
            startIcon={<TableRowsIcon sx={{ fontSize: 14 }} />}
            onClick={() => setViewMode("flat")}
            sx={{ textTransform: "none", fontSize: 12, fontWeight: 600, borderRadius: 1.5,
              ...(viewMode === "flat"
                ? { bgcolor: "#2f3891", "&:hover": { bgcolor: "#252e7a" }, boxShadow: "none" }
                : { borderColor: "#e2e8f0", color: "#475569", "&:hover": { borderColor: "#c4b5fd", bgcolor: "#faf5ff" } }) }}>
            Flat View
          </Button>
          <Button
            size="small"
            variant={viewMode === "grouped" ? "contained" : "outlined"}
            startIcon={<ViewAgendaOutlinedIcon sx={{ fontSize: 14 }} />}
            onClick={() => setViewMode("grouped")}
            sx={{ textTransform: "none", fontSize: 12, fontWeight: 600, borderRadius: 1.5,
              ...(viewMode === "grouped"
                ? { bgcolor: "#2f3891", "&:hover": { bgcolor: "#252e7a" }, boxShadow: "none" }
                : { borderColor: "#e2e8f0", color: "#475569", "&:hover": { borderColor: "#c4b5fd", bgcolor: "#faf5ff" } }) }}>
            Grouped View
          </Button>
        </Box>
        {viewMode === "grouped" && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>Group by:</Typography>
            {(["client", "company", "driver", "route"] as const).map((opt) => (
              <Chip key={opt}
                label={opt.charAt(0).toUpperCase() + opt.slice(1)}
                size="small"
                onClick={() => setGroupBy(opt)}
                sx={{
                  height: 24, fontSize: 11, fontWeight: 700, cursor: "pointer",
                  bgcolor: groupBy === opt ? "#2f3891" : "#f1f5f9",
                  color: groupBy === opt ? "#fff" : "#475569",
                  "&:hover": { bgcolor: groupBy === opt ? "#252e7a" : "#e2e8f0" },
                }} />
            ))}
            <Button size="small" variant="outlined" startIcon={<DownloadIcon sx={{ fontSize: 13 }} />}
              onClick={exportGroupedExcel}
              sx={{ textTransform: "none", fontSize: 12, borderRadius: 1.5, borderColor: "#e2e8f0", color: "#475569" }}>
              Export
            </Button>
          </Box>
        )}
      </Box>

      {/* ── Grouped View ──────────────────────────────────────────────────── */}
      {viewMode === "grouped" && (
        <Box sx={{ mb: 3 }}>
          {groupedData.length === 0 ? (
            <Card sx={{ borderRadius: 2.5, border: "1px solid #f1f5f9", p: 5, textAlign: "center" }}>
              <Typography sx={{ color: "#94a3b8", fontSize: 13 }}>No data found for the selected filters.</Typography>
            </Card>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {groupedData.map((group) => {
                const balance     = Math.max(0, group.totalAmount - group.totalPaid);
                const isExpanded  = expandedGroups.has(group.key);
                const paidPct     = group.totalAmount > 0 ? (group.totalPaid / group.totalAmount) * 100 : 0;
                const statusColor = paidPct >= 100 ? "#16a34a" : paidPct > 0 ? "#0284c7" : "#dc2626";
                const statusBg    = paidPct >= 100 ? "#dcfce7" : paidPct > 0 ? "#dbeafe" : "#fee2e2";
                const statusLabel = paidPct >= 100 ? "Fully Paid" : paidPct > 0 ? "Partial" : "Outstanding";

                return (
                  <Card key={group.key} sx={{ borderRadius: 2.5, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 6px rgba(0,0,0,0.06)" }}>
                    {/* Group header */}
                    <Box
                      onClick={() => toggleGroup(group.key)}
                      sx={{ px: 2.5, py: 2, display: "flex", alignItems: "center", justifyContent: "space-between",
                        cursor: "pointer", bgcolor: isExpanded ? "#faf5ff" : "#fff",
                        borderBottom: isExpanded ? "1px solid #e2e8f0" : "none",
                        "&:hover": { bgcolor: "#faf5ff" }, transition: "background 0.15s" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Box sx={{ width: 38, height: 38, borderRadius: 2, bgcolor: "#ede9fe", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <Typography sx={{ fontSize: 15, fontWeight: 800, color: "#2f3891" }}>
                            {group.key.charAt(0).toUpperCase()}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography sx={{ fontWeight: 700, fontSize: 13.5, color: "#1e293b" }}>{group.key}</Typography>
                          <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.2 }}>
                            {group.invoices.length} invoice{group.invoices.length !== 1 ? "s" : ""}
                          </Typography>
                        </Box>
                        <Chip label={statusLabel} size="small"
                          sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: statusBg, color: statusColor, border: `1px solid ${statusColor}33`, ml: 1 }} />
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 3, flexShrink: 0 }}>
                        <Box sx={{ textAlign: "right" }}>
                          <Typography sx={{ fontSize: 10, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Revenue</Typography>
                          <Typography sx={{ fontSize: 14, fontWeight: 800, color: "#1e293b" }}>${group.totalAmount.toFixed(2)}</Typography>
                        </Box>
                        <Box sx={{ textAlign: "right" }}>
                          <Typography sx={{ fontSize: 10, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Paid</Typography>
                          <Typography sx={{ fontSize: 14, fontWeight: 800, color: "#16a34a" }}>${group.totalPaid.toFixed(2)}</Typography>
                        </Box>
                        <Box sx={{ textAlign: "right" }}>
                          <Typography sx={{ fontSize: 10, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Balance</Typography>
                          <Typography sx={{ fontSize: 14, fontWeight: 800, color: balance === 0 ? "#16a34a" : "#dc2626" }}>${balance.toFixed(2)}</Typography>
                        </Box>
                        <Box sx={{ textAlign: "right" }}>
                          <Typography sx={{ fontSize: 10, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>HST</Typography>
                          <Typography sx={{ fontSize: 14, fontWeight: 800, color: "#ea580c" }}>${group.totalHST.toFixed(2)}</Typography>
                        </Box>
                        <IconButton size="small" sx={{ color: "#94a3b8" }}>
                          {isExpanded ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
                        </IconButton>
                      </Box>
                    </Box>

                    {/* Progress bar */}
                    <Box sx={{ height: 3, bgcolor: "#f1f5f9" }}>
                      <Box sx={{ height: 3, width: `${Math.min(100, paidPct)}%`, bgcolor: statusColor, transition: "width 0.4s" }} />
                    </Box>

                    {/* Expandable invoice table */}
                    <Collapse in={isExpanded}>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              {["Invoice No", "Date", "Driver(s)", "Trip Route", "Amount", "Paid", "Balance", "Status", ""].map((h, i) => (
                                <TableCell key={i} sx={{ ...hdrSx, bgcolor: "#fdf4ff" }}>{h}</TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {group.invoices.map((inv) => {
                              const st      = getStatus(inv.quoteWorkOrderStatus);
                              const total   = parseFloat(inv.quoteWorkOrderSaleGrandTotal || "0");
                              const paid    = parseFloat(String(inv.amount_paid || "0"));
                              const bal     = Math.max(0, total - paid);
                              const drivers = Array.isArray(inv.drivers) && inv.drivers.length
                                ? inv.drivers.map((d: any) => `${cap(d.driverFirstName)} ${cap(d.driverLastName)}`).join(", ")
                                : "—";
                              const route   = Array.isArray(inv.quoteWorkOrderSale) && inv.quoteWorkOrderSale.length
                                ? inv.quoteWorkOrderSale.map((s: any) => s?.transferTypeFrom && s?.transferTypeTo ? `${s.transferTypeFrom} → ${s.transferTypeTo}` : null).filter(Boolean).join(", ") || "—"
                                : "—";
                              return (
                                <TableRow key={inv.id} sx={{ "&:hover": { bgcolor: "#fdf4ff" } }}>
                                  <TableCell sx={{ ...cellSx, fontWeight: 700 }}>{inv.InvoiceNo || "—"}</TableCell>
                                  <TableCell sx={cellSx}>{moment(inv.created_at).format("DD-MMM-YY")}</TableCell>
                                  <TableCell sx={{ ...cellSx, color: "#475569" }}>{drivers}</TableCell>
                                  <TableCell sx={{ ...cellSx, color: "#475569", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{route}</TableCell>
                                  <TableCell sx={cellSx}>${total.toFixed(2)}</TableCell>
                                  <TableCell sx={{ ...cellSx, color: "#16a34a", fontWeight: 600 }}>${paid.toFixed(2)}</TableCell>
                                  <TableCell sx={{ ...cellSx, color: bal === 0 ? "#16a34a" : "#dc2626", fontWeight: 600 }}>${bal.toFixed(2)}</TableCell>
                                  <TableCell sx={cellSx}>
                                    <Chip label={st.label} size="small"
                                      sx={{ height: 20, fontSize: 10, fontWeight: 700, color: st.color, bgcolor: st.bg, border: `1px solid ${st.color}33` }} />
                                  </TableCell>
                                  <TableCell sx={{ ...cellSx, textAlign: "right" }}>
                                    <Tooltip title="View invoice" arrow>
                                      <IconButton size="small" onClick={() => navigate(`/invoice/history/${inv.id}`)}
                                        sx={{ color: "#2f3891", bgcolor: "#ede9fe", borderRadius: 1, width: 26, height: 26, "&:hover": { bgcolor: "#ddd6fe" } }}>
                                        <VisibilitySharpIcon sx={{ fontSize: 13 }} />
                                      </IconButton>
                                    </Tooltip>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                            {/* Group subtotal row */}
                            <TableRow sx={{ bgcolor: "#f5f3ff" }}>
                              <TableCell sx={{ ...cellSx, fontWeight: 800, color: "#2f3891" }} colSpan={4}>
                                Subtotal — {group.invoices.length} invoice{group.invoices.length !== 1 ? "s" : ""}
                              </TableCell>
                              <TableCell sx={{ ...cellSx, fontWeight: 800 }}>${group.totalAmount.toFixed(2)}</TableCell>
                              <TableCell sx={{ ...cellSx, fontWeight: 800, color: "#16a34a" }}>${group.totalPaid.toFixed(2)}</TableCell>
                              <TableCell sx={{ ...cellSx, fontWeight: 800, color: balance === 0 ? "#16a34a" : "#dc2626" }}>
                                ${balance.toFixed(2)}
                              </TableCell>
                              <TableCell sx={cellSx} colSpan={2} />
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Collapse>
                  </Card>
                );
              })}

              {/* Grand total bar */}
              <Card sx={{ borderRadius: 2.5, border: "1px solid #e2e8f0", bgcolor: "#faf5ff", px: 2.5, py: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: 13, color: "#2f3891" }}>
                    Grand Total — {groupedData.length} group{groupedData.length !== 1 ? "s" : ""}, {filteredCoursings.length} invoices
                  </Typography>
                  <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                    {[
                      { label: "Revenue",  value: `$${filteredCoursings.reduce((s, i) => s + parseFloat(i.quoteWorkOrderSaleGrandTotal || "0"), 0).toFixed(2)}`, color: "#1e293b" },
                      { label: "Paid",     value: `$${filteredCoursings.reduce((s, i) => s + parseFloat(String(i.amount_paid || "0")), 0).toFixed(2)}`, color: "#16a34a" },
                      { label: "Balance",  value: `$${filteredCoursings.reduce((s, i) => s + Math.max(0, parseFloat(i.quoteWorkOrderSaleGrandTotal || "0") - parseFloat(String(i.amount_paid || "0"))), 0).toFixed(2)}`, color: "#dc2626" },
                      { label: "HST",      value: `$${totalHST.toFixed(2)}`, color: "#ea580c" },
                    ].map((item) => (
                      <Box key={item.label} sx={{ textAlign: "right" }}>
                        <Typography sx={{ fontSize: 10, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>{item.label}</Typography>
                        <Typography sx={{ fontSize: 15, fontWeight: 800, color: item.color }}>{item.value}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Card>
            </Box>
          )}
        </Box>
      )}

      {/* ── Flat View tables (hidden in grouped mode) ──────────────────────── */}
      {viewMode === "flat" && <>

      {/* ── Invoices table ────────────────────────────────────────────────── */}
      <Card sx={{ borderRadius: 2.5, boxShadow: "0 1px 8px rgba(0,0,0,0.07)", border: "1px solid #f1f5f9", mb: 3 }}>
        <SectionHeader accent="#2f3891" title="Invoices" actions={
          filteredCoursings.length > 0 ? (
            <>
              <Button size="small" variant="outlined"
                startIcon={pdfLoading ? <CircularProgress size={13} color="inherit" /> : <LocalPrintshopSharpIcon sx={{ fontSize: 13 }} />}
                onClick={handleDownloadPdf} disabled={pdfLoading}
                sx={{ textTransform: "none", fontSize: 12, borderRadius: 1.5, borderColor: "#e2e8f0", color: "#475569" }}>
                {pdfLoading ? "Generating…" : "Print"}
              </Button>
              <Button size="small" variant="outlined" startIcon={<DownloadIcon sx={{ fontSize: 13 }} />}
                onClick={exportInvoicesExcel}
                sx={{ textTransform: "none", fontSize: 12, borderRadius: 1.5, borderColor: "#e2e8f0", color: "#475569" }}>
                Excel
              </Button>
            </>
          ) : undefined
        } />

        <div ref={componentRef}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {["#", "Invoice No", "Status", "Amount", "Paid", "Balance", "Driver(s)", "Trip", "Issued By", "Client", ""].map((h, i) => (
                    <TableCell key={i} sx={{ ...hdrSx, textAlign: i === 10 ? "right" : "left" }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {isError || filteredCoursings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 4, color: "#94a3b8", fontSize: 13 }}>
                      {isError ? "Error fetching data" : "No invoices found"}
                    </TableCell>
                  </TableRow>
                ) : filteredCoursings
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row: IInvoice, index) => {
                      const st       = getStatus(row.quoteWorkOrderStatus);
                      const grandTotal = parseFloat(row.quoteWorkOrderSaleGrandTotal || "0");
                      const paid       = parseFloat(String(row.amount_paid || "0"));
                      const balance    = Math.max(0, grandTotal - paid);
                      const driverList = Array.isArray(row?.drivers) && row.drivers.length
                        ? row.drivers.map((d: any) => `${cap(d.driverFirstName)} ${cap(d.driverLastName)}`).join(", ")
                        : "—";
                      const tripInfo = Array.isArray(row.quoteWorkOrderSale) && row.quoteWorkOrderSale.length
                        ? row.quoteWorkOrderSale
                            .map((s: any) => s?.transferTypeFrom && s?.transferTypeTo ? `${s.transferTypeFrom} → ${s.transferTypeTo}` : null)
                            .filter((v, i, arr) => v !== null && arr.indexOf(v) === i)
                            .join(", ") || "—"
                        : "—";
                      const clientName = row.project?.projectName
                        || row.customer?.customerCompanyName
                        || `${cap(row.customer?.customerFirstName)} ${cap(row.customer?.customerLastName)}`.trim()
                        || "—";

                      return (
                        <TableRow key={row.id} sx={{ "&:hover": { bgcolor: "#fafcff" } }}>
                          <TableCell sx={cellSx}>{page * rowsPerPage + index + 1}</TableCell>
                          <TableCell sx={{ ...cellSx, fontWeight: 700 }}>{row.InvoiceNo || "—"}</TableCell>
                          <TableCell sx={cellSx}>
                            <Chip label={st.label} size="small"
                              sx={{ height: 20, fontSize: 10, fontWeight: 700, color: st.color, bgcolor: st.bg, border: `1px solid ${st.color}33` }} />
                          </TableCell>
                          <TableCell sx={cellSx}>${grandTotal.toFixed(2)}</TableCell>
                          <TableCell sx={{ ...cellSx, color: paid > 0 ? "#16a34a" : "#64748b", fontWeight: paid > 0 ? 600 : 400 }}>
                            ${paid.toFixed(2)}
                          </TableCell>
                          <TableCell sx={{ ...cellSx, color: balance === 0 ? "#16a34a" : "#dc2626", fontWeight: 600 }}>
                            ${balance.toFixed(2)}
                          </TableCell>
                          <TableCell sx={{ ...cellSx, color: "#475569" }}>{driverList}</TableCell>
                          <TableCell sx={{ ...cellSx, color: "#475569", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tripInfo}</TableCell>
                          <TableCell sx={cellSx}>{(row.company as any)?.companyName || "—"}</TableCell>
                          <TableCell sx={cellSx}>{clientName}</TableCell>
                          <TableCell sx={{ ...cellSx, textAlign: "right" }} className="no-print">
                            <Button size="small" variant="outlined"
                              onClick={() => navigate(`/invoice/history/${row.id}`)}
                              sx={{ minWidth: 0, p: "4px 8px", borderRadius: 1.5, borderColor: "#e2e8f0", color: "#2f3891", "&:hover": { borderColor: "#c4b5fd", bgcolor: "#faf5ff" } }}>
                              <VisibilitySharpIcon sx={{ fontSize: 14 }} />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ display: "flex", justifyContent: "flex-end", px: 2, py: 1.5 }} className="no-print">
            <Pagination count={filteredCoursings.length} page={page} rowsPerPage={rowsPerPage} onPageChange={(_e, p) => setPage(p)} />
          </Box>
        </div>
      </Card>

      {/* ── HST Report ───────────────────────────────────────────────────── */}
      <Card sx={{ borderRadius: 2.5, boxShadow: "0 1px 8px rgba(0,0,0,0.07)", border: "1px solid #f1f5f9", mb: 3 }}>
        <SectionHeader accent="#ea580c" title="HST Tax Report" actions={
          filteredCoursings.length > 0 ? (
            <>
              <Button size="small" variant="outlined"
                startIcon={pdfLoading ? <CircularProgress size={13} color="inherit" /> : <LocalPrintshopSharpIcon sx={{ fontSize: 13 }} />}
                onClick={handleDownloadPdf} disabled={pdfLoading}
                sx={{ textTransform: "none", fontSize: 12, borderRadius: 1.5, borderColor: "#e2e8f0", color: "#475569" }}>
                {pdfLoading ? "Generating…" : "Print"}
              </Button>
              <Button size="small" variant="outlined" startIcon={<DownloadIcon sx={{ fontSize: 13 }} />}
                onClick={() => {
                  const rows = filteredCoursings
                    .map((row, i) => {
                      const rowHST = Array.isArray(row.quoteWorkOrderSale)
                        ? row.quoteWorkOrderSale.reduce((ts: number, p: any) => ts + Number(p.taxAmount || 0), 0)
                        : 0;
                      return {
                        "#": i + 1,
                        "Invoice No": row.InvoiceNo || "—",
                        "Client": `${cap(row.customer?.customerFirstName)} ${cap(row.customer?.customerLastName)}`.trim() || row.project?.projectName || "—",
                        "Invoice Amount": `$${parseFloat(row.quoteWorkOrderSaleGrandTotal || "0").toFixed(2)}`,
                        "HST Tax": `$${rowHST.toFixed(2)}`,
                        "Date": moment(row.created_at).format("DD-MMM-YY"),
                      };
                    });
                  rows.push({ "#": "" as any, "Invoice No": "", "Client": "TOTAL HST", "Invoice Amount": "", "HST Tax": `$${totalHST.toFixed(2)}`, "Date": "" });
                  const ws = XLSX.utils.json_to_sheet(rows);
                  const wb = XLSX.utils.book_new();
                  XLSX.utils.book_append_sheet(wb, ws, "HST Report");
                  XLSX.writeFile(wb, "tjbus_hst_report.xlsx");
                }}
                sx={{ textTransform: "none", fontSize: 12, borderRadius: 1.5, borderColor: "#e2e8f0", color: "#475569" }}>
                Excel
              </Button>
            </>
          ) : undefined
        } />

        <div ref={hstRef}>
          {/* Print-only header */}
          <Box sx={{ display: "none", "@media print": { display: "block" }, px: 3, py: 2, borderBottom: "1px solid #e2e8f0" }}>
            <Typography sx={{ fontWeight: 800, fontSize: 16, color: "#0f172a" }}>TJ Bus — HST Tax Report</Typography>
            {(startDate || endDate) && (
              <Typography sx={{ fontSize: 12, color: "#64748b", mt: 0.5 }}>
                Period: {startDate ? moment(startDate).format("DD MMM YYYY") : ""}{endDate && endDate !== startDate ? ` – ${moment(endDate).format("DD MMM YYYY")}` : ""}
              </Typography>
            )}
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {["#", "Invoice No", "Date", "Client", "Invoice Amount", "HST Tax"].map((h) => (
                    <TableCell key={h} sx={hdrSx}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCoursings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: "#94a3b8", fontSize: 13 }}>No data found</TableCell>
                  </TableRow>
                ) : (
                  <>
                    {filteredCoursings
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((row, index) => {
                        const rowHST = Array.isArray(row.quoteWorkOrderSale)
                          ? row.quoteWorkOrderSale.reduce((ts: number, p: any) => ts + Number(p.taxAmount || 0), 0)
                          : 0;
                        const clientName = row.project?.projectName
                          || row.customer?.customerCompanyName
                          || `${cap(row.customer?.customerFirstName)} ${cap(row.customer?.customerLastName)}`.trim()
                          || "—";
                        return (
                          <TableRow key={row.id} sx={{ "&:hover": { bgcolor: "#fff7ed" } }}>
                            <TableCell sx={cellSx}>{page * rowsPerPage + index + 1}</TableCell>
                            <TableCell sx={{ ...cellSx, fontWeight: 700 }}>{row.InvoiceNo || "—"}</TableCell>
                            <TableCell sx={cellSx}>{moment(row.created_at).format("DD-MMM-YY")}</TableCell>
                            <TableCell sx={cellSx}>{clientName}</TableCell>
                            <TableCell sx={cellSx}>${parseFloat(row.quoteWorkOrderSaleGrandTotal || "0").toFixed(2)}</TableCell>
                            <TableCell sx={{ ...cellSx, fontWeight: 700, color: rowHST > 0 ? "#ea580c" : "#94a3b8" }}>
                              ${rowHST.toFixed(2)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    {/* Total row */}
                    <TableRow sx={{ bgcolor: "#fff7ed" }}>
                      <TableCell sx={{ ...cellSx, fontWeight: 700 }} colSpan={5}>Total HST</TableCell>
                      <TableCell sx={{ ...cellSx, fontWeight: 800, color: "#ea580c", fontSize: 13 }}>
                        ${totalHST.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ display: "flex", justifyContent: "flex-end", px: 2, py: 1.5 }} className="no-print">
            <Pagination count={filteredCoursings.length} page={page} rowsPerPage={rowsPerPage} onPageChange={(_e, p) => setPage(p)} />
          </Box>
        </div>
      </Card>

      {/* ── Payments table ────────────────────────────────────────────────── */}
      <Card sx={{ borderRadius: 2.5, boxShadow: "0 1px 8px rgba(0,0,0,0.07)", border: "1px solid #f1f5f9" }}>
        <SectionHeader accent="#10b981" title="Payments" actions={
          filteredPayments.length > 0 ? (
            <>
              <Button size="small" variant="outlined"
                startIcon={pdfLoading ? <CircularProgress size={13} color="inherit" /> : <LocalPrintshopSharpIcon sx={{ fontSize: 13 }} />}
                onClick={handleDownloadPdf} disabled={pdfLoading}
                sx={{ textTransform: "none", fontSize: 12, borderRadius: 1.5, borderColor: "#e2e8f0", color: "#475569" }}>
                {pdfLoading ? "Generating…" : "Print"}
              </Button>
              <Button size="small" variant="outlined" startIcon={<DownloadIcon sx={{ fontSize: 13 }} />}
                onClick={exportPaymentsExcel}
                sx={{ textTransform: "none", fontSize: 12, borderRadius: 1.5, borderColor: "#e2e8f0", color: "#475569" }}>
                Excel
              </Button>
            </>
          ) : undefined
        } />

        <div ref={paymentRef}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {["Date", "Invoice No", "Amount", "Method"].map((h) => (
                    <TableCell key={h} sx={hdrSx}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPayments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4, color: "#94a3b8", fontSize: 13 }}>No payments found</TableCell>
                  </TableRow>
                ) : (
                  <>
                    {filteredPayments
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((row) => (
                        <TableRow key={row.id} sx={{ "&:hover": { bgcolor: "#f0fdf4" } }}>
                          <TableCell sx={cellSx}>{moment(row.payment_date).format("DD-MMM-YY")}</TableCell>
                          <TableCell sx={{ ...cellSx, fontWeight: 600 }}>{row.InvoiceNo || "—"}</TableCell>
                          <TableCell sx={{ ...cellSx, color: "#16a34a", fontWeight: 700 }}>
                            ${parseFloat(String(row.amount || "0")).toFixed(2)}
                          </TableCell>
                          <TableCell sx={cellSx}>
                            <Chip label={row.payment_method} size="small"
                              sx={{ height: 20, fontSize: 10, fontWeight: 600,
                                bgcolor: /po|purchase/i.test(row.payment_method) ? "#fef3c7" : /cash/i.test(row.payment_method) ? "#f0fdf4" : "#dbeafe",
                                color:  /po|purchase/i.test(row.payment_method) ? "#d97706" : /cash/i.test(row.payment_method) ? "#16a34a" : "#2563eb",
                              }} />
                          </TableCell>
                        </TableRow>
                      ))}
                    {/* Total row */}
                    <TableRow sx={{ bgcolor: "#f8fafc" }}>
                      <TableCell sx={{ ...cellSx, fontWeight: 700 }} colSpan={2}>Total</TableCell>
                      <TableCell sx={{ ...cellSx, fontWeight: 800, color: "#16a34a", fontSize: 13 }}>
                        ${totalPaymentsSum.toFixed(2)}
                      </TableCell>
                      <TableCell sx={cellSx} />
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ display: "flex", justifyContent: "flex-end", px: 2, py: 1.5 }} className="no-print">
            <Pagination count={filteredPayments.length} page={page} rowsPerPage={rowsPerPage} onPageChange={(_e, p) => setPage(p)} />
          </Box>
        </div>
      </Card>

      </> /* end flat view */}

    </Box>
  );
};

export default Index;
